import React, { useState, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { Plus, FileText, Upload, Sparkles, RotateCcw } from "lucide-react";
import { useAnimatedPlaceholder } from "@/hooks/useAnimatedPlaceholder";
import { useAutoResizeTextarea } from "@/hooks/useAutoResizeTextarea";
import { useTypingAnimation } from "@/hooks/useTypingAnimation";


interface DocumentCreationInterfaceProps {
  onCreateDocument: (input: string, files?: File[]) => void;
  onShowTemplates: () => void;
  disabled?: boolean;
}

const DocumentCreationInterface = ({ 
  onCreateDocument, 
  onShowTemplates, 
  disabled = false 
}: DocumentCreationInterfaceProps) => {
  const [input, setInput] = useState("");
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [documentMode, setDocumentMode] = useState<'normal' | 'peticao' | 'contestacao'>('normal');
  const [isContestacaoBlocked, setIsContestacaoBlocked] = useState(true);
  const placeholder = useAnimatedPlaceholder();
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  
  const peticaoPlaceholder = useTypingAnimation({
    text: "Descreva os fatos com o máximo de detalhes e envie os documentos para criar...",
    enabled: documentMode === 'peticao'
  });
  
  const contestacaoPlaceholder = useTypingAnimation({
    text: "Envie a Petição Inicial para começar a contestação",
    enabled: documentMode === 'contestacao' && isContestacaoBlocked
  });

  useAutoResizeTextarea({
    textareaRef,
    value: input,
    minHeight: 40,
    maxHeight: 240
  });

  const getPlaceholder = () => {
    if (documentMode === 'peticao') {
      return peticaoPlaceholder;
    }
    if (documentMode === 'contestacao') {
      return isContestacaoBlocked 
        ? contestacaoPlaceholder
        : 'Descreva como quer estruturar a contestação...';
    }
    return placeholder; // Placeholder animado normal
  };

  const handlePeticaoInicial = () => {
    setDocumentMode('peticao');
    setInput('');
  };

  const handleContestacao = () => {
    setDocumentMode('contestacao');
    setIsContestacaoBlocked(true);
    setInput('');
    setSelectedFiles([]);
  };

  const handleResetMode = () => {
    setDocumentMode('normal');
    setIsContestacaoBlocked(true);
    setInput('');
  };

  const handleDocEmBranco = () => {
    onCreateDocument('__BLANK_DOCUMENT__', []);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (documentMode === 'contestacao' && isContestacaoBlocked) return;
    
    if (input.trim() || selectedFiles.length > 0) {
      let finalInput = input.trim();
      
      // Adiciona contexto específico baseado no modo
      if (documentMode === 'peticao') {
        finalInput = `INSTRUÇÃO: Gere uma petição inicial com as informações detalhadas fornecidas. Inclua todos os elementos necessários de uma petição inicial.\n\n${finalInput}`;
      } else if (documentMode === 'contestacao') {
        finalInput = `INSTRUÇÃO: Analise a petição inicial anexada e gere uma contestação detalhada baseada nas informações fornecidas.\n\n${finalInput}`;
      }
      
      console.log('📝 DocumentCreationInterface: Criando documento com input:', finalInput.substring(0, 100));
      onCreateDocument(finalInput, selectedFiles);
      setInput("");
      setSelectedFiles([]);
      setDocumentMode('normal');
      setIsContestacaoBlocked(true);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  const handleFilesSelected = (files: File[]) => {
    setSelectedFiles(files);
  };

  const handleRemoveFile = (index: number) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleAttachmentClick = () => {
    if (disabled) return;
    const input = document.createElement('input');
    input.type = 'file';
    input.multiple = true;
    input.accept = '.pdf,.png,.jpg,.jpeg,.doc,.docx,.txt';
    input.onchange = (e) => {
      const target = e.target as HTMLInputElement;
      const files = Array.from(target.files || []);
      if (files.length > 0) {
        setSelectedFiles(prev => [...prev, ...files]);
        // Verifica se há upload para contestação
        if (documentMode === 'contestacao' && files.length > 0) {
          setIsContestacaoBlocked(false);
        }
      }
      target.value = '';
    };
    input.click();
  };

  return (
    <div className="document-creation-container relative z-10 isolation-isolate space-y-6 px-4 mb-10 md:mb-16">
      {/* Title */}
      <div className="text-center space-y-2">
        <h1 className="text-4xl font-bold text-foreground">
          Crie qualquer documento jurídico
        </h1>
        <p className="text-muted-foreground text-lg">
          Descreva o que você precisa ou faça upload de arquivos para começar
        </p>
      </div>

      {/* Botões de Modo */}
      <div className="flex justify-center items-center gap-3 w-full max-w-4xl">
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={handleDocEmBranco}
          disabled={disabled}
          className="px-4 py-2 h-10 text-sm"
        >
          📄 Doc em Branco
        </Button>
        
        <Button
          type="button"
          variant={documentMode === 'peticao' ? 'default' : 'outline'}
          size="sm"
          onClick={handlePeticaoInicial}
          disabled={disabled}
          className="px-4 py-2 h-10 text-sm"
          data-tour="caso-btn-peticao"
        >
          📋 Petição Inicial
        </Button>
        
        <Button
          type="button"
          variant={documentMode === 'contestacao' ? 'default' : 'outline'}
          size="sm"
          onClick={handleContestacao}
          disabled={disabled}
          className="px-4 py-2 h-10 text-sm"
        >
          ⚖️ Contestação
        </Button>
        
        {documentMode !== 'normal' && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={handleResetMode}
            className="h-10 px-3 text-sm"
          >
            <RotateCcw className="h-4 w-4 mr-1" />
            Voltar
          </Button>
        )}
      </div>

      {/* Aviso para Petição Inicial */}
      {documentMode === 'peticao' && (
        <div className="w-full max-w-4xl bg-blue-50 border border-blue-200 rounded-md p-4">
          <div className="flex items-center gap-2 text-blue-800">
            <Upload className="h-5 w-5" />
            <span className="font-medium">Envie documentos nos formatos JPG, JPEG, PNG, PDF, TXT, DOC ou DOCX. Os textos serão extraídos automaticamente desses arquivos, inclusive de fotos e imagens</span>
          </div>
        </div>
      )}

      {/* Aviso para Contestação */}
      {documentMode === 'contestacao' && isContestacaoBlocked && (
        <div className="w-full max-w-4xl bg-amber-50 border border-amber-200 rounded-md p-4">
          <div className="flex items-center gap-2 text-amber-800">
            <Upload className="h-5 w-5" />
            <span className="font-medium">Envie a Petição Inicial para começar a contestação</span>
          </div>
        </div>
      )}

      {/* Main Input Card */}
      <Card className="w-full max-w-4xl p-6 border border-border bg-card overflow-visible">
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Textarea with buttons */}
          <div className="relative">
            <Textarea
              ref={textareaRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={getPlaceholder()}
              className="pr-64 text-lg resize-none overflow-hidden"
              style={{ minHeight: '40px' }}
              disabled={disabled || (documentMode === 'contestacao' && isContestacaoBlocked)}
              rows={1}
            />
            <div className="absolute right-2 top-1/2 -translate-y-1/2 pointer-events-auto z-20 flex items-center space-x-2 bg-card shadow-sm rounded-md">
              {/* Upload Button */}
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleAttachmentClick}
                disabled={disabled}
                className="h-8 w-8 p-0"
                data-tour="caso-btn-upload-multiple"
              >
                <Plus className="h-4 w-4" />
              </Button>
              
              {/* Templates Button */}
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={onShowTemplates}
                disabled={disabled}
                className="px-3 py-1 h-8 text-xs"
                data-tour="caso-btn-modelos"
              >
                <FileText className="h-3 w-3 mr-1" />
                Modelos
              </Button>
              
              {/* Submit Button */}
              {(input.trim() || selectedFiles.length > 0) && (
                <Button
                  type="submit"
                  size="sm"
                  disabled={disabled}
                  className="h-8 px-3"
                >
                  <Sparkles className="h-3 w-3 mr-1" />
                  Criar
                </Button>
              )}
            </div>
          </div>

          {/* Selected Files Preview */}
          {selectedFiles.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {selectedFiles.map((file, index) => (
                <div
                  key={index}
                  className="flex items-center gap-2 bg-muted px-3 py-1 rounded-md text-sm"
                >
                  <Upload className="h-3 w-3" />
                  <span className="truncate max-w-32">{file.name}</span>
                  <button
                    type="button"
                    onClick={() => handleRemoveFile(index)}
                    className="hover:text-destructive"
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          )}
        </form>
      </Card>


      {/* Features */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 w-full max-w-4xl">
        <div className="text-center p-4 bg-card/80 backdrop-blur-sm border border-border/20 rounded-lg">
          <Sparkles className="h-8 w-8 mx-auto mb-2 text-primary" />
          <h3 className="font-semibold mb-1 text-card-foreground">IA Especializada</h3>
          <p className="text-sm text-muted-foreground">
            Criação inteligente de documentos jurídicos
          </p>
        </div>
        <div className="text-center p-4 bg-card/80 backdrop-blur-sm border border-border/20 rounded-lg">
          <Upload className="h-8 w-8 mx-auto mb-2 text-primary" />
          <h3 className="font-semibold mb-1 text-card-foreground">Upload de Arquivos</h3>
          <p className="text-sm text-muted-foreground">
            Analise PDFs e imagens para criar documentos
          </p>
        </div>
        <div className="text-center p-4 bg-card/80 backdrop-blur-sm border border-border/20 rounded-lg">
          <FileText className="h-8 w-8 mx-auto mb-2 text-primary" />
          <h3 className="font-semibold mb-1 text-card-foreground">Modelos Prontos</h3>
          <p className="text-sm text-muted-foreground">
            Acesse centenas de templates jurídicos
          </p>
        </div>
      </div>
    </div>
  );
};

export default DocumentCreationInterface;